import moment from 'moment'
import { DATE_TIME_FORMAT, DATE_TIME_FORMAT_API } from 'containers/HomePage/containers/TaskList/constant'
import { BookingStatuses, GeneralBookingWaitingStatuses } from 'constants/misc'

type Booking = {
  booking_id: string
  repeat_id?: string
  berth_id?: string
  status: number
  start_date: string | null
  start_time: string | null
  end_date: string | null
  end_time: string | null
  checkin_date: string | null // 受付日時
  plan_start_date: string | null // 予約開始日付
  plan_start_time: string | null // 予約開始時刻
  calling_date: string | null
  calling_time: string | null
  driver_id: string | null
  driver_phone: string | null
  entry_instruction_time?: string // 入場指示日時
  general_booking_id?: number
  general_booking_waiting_status?: number
  general_booking_start_date?: string // 総合予約開始日時
  general_booking_first_start_time?: string // 総合1巡目の作業開始時間
  general_entry_instruction_time?: string // 総合入場指示日時
  checkin_date_of_general_booking?: string // 総合受付日時
  isYesterday?: boolean
}

export const sortByGeneralBookingStayTime = (bookings: Booking[]) => {
  const result = [...bookings]

  result.sort((a, b) => {
    /**
     * 第1ソート（グルーピング）：ステータス
     * -> 操作できるカード（綜合の場合は順番になっている）カードを優先
     */
    const checkIfOperable = (booking: Booking) => {
      // OPERATABLE -> NOT_OPERATABLEの順にソートしたいのでそれぞれ0,1を割り当てる
      const OPERATABLE = 0
      const NOT_OPERATABLE = 1

      // 総合予約かつ未割当なら、操作不可
      if (booking.general_booking_id && booking.status === BookingStatuses.Unassigned.id)
        return NOT_OPERATABLE

      // 総合予約のgeneral_booking_waiting_statusが0以上なら、操作不可
      if (booking.general_booking_waiting_status != null && booking.general_booking_waiting_status > GeneralBookingWaitingStatuses.WAITING_ZERO.id)
        return NOT_OPERATABLE

      // それ以外は操作可能
      return OPERATABLE
    }

    const operatableA = checkIfOperable(a)
    const operatableB = checkIfOperable(b)
    if (operatableA < operatableB) return -1 // aが先
    if (operatableA > operatableB) return 1 // bが先

    /**
     * 第2ソート：各種時間
     * -> min(
     *   max(予約時刻, 指示時刻, 受付時間),
     *   max(総合予約時刻, 総合指示時刻, 総合受付時間),
     * )
     */
    const getMaxTimeGeneralBooking = (booking: Booking) => {
      const validTimes = [
        // 総合受付時間
        moment(booking.checkin_date_of_general_booking, DATE_TIME_FORMAT),
        // 総合入場指示時刻
        moment(booking.general_entry_instruction_time, DATE_TIME_FORMAT),
        // 総合予約開始時間
        moment(booking.general_booking_start_date, DATE_TIME_FORMAT),
      ].filter((m) => m.isValid())

      if (validTimes.length === 0) return null

      return moment.max(validTimes)
    }
    const maxTimeGeneralBookingA = getMaxTimeGeneralBooking(a)
    const maxTimeGeneralBookingB = getMaxTimeGeneralBooking(b)

    const getMaxTimeNormalBooking = (booking: Booking) => {
      const validTimes = [
        // 受付時間
        moment(booking.checkin_date, DATE_TIME_FORMAT),
        // 入場指示時刻
        moment(booking.entry_instruction_time, DATE_TIME_FORMAT),
        // 予約開始時間
        moment(`${booking.plan_start_date} ${booking.plan_start_time}`, DATE_TIME_FORMAT_API),
      ].filter((m) => m.isValid())

      if (validTimes.length === 0) return null

      return moment.max(validTimes)
    }
    const maxTimeNormalBookingA = getMaxTimeNormalBooking(a)
    const maxTimeNormalBookingB = getMaxTimeNormalBooking(b)

    const getMinTime = (
      maxTimeNormalBooking: moment.Moment | null,
      maxTimeGeneralBooking: moment.Moment | null
    ) => {
      const validTimes = [
        maxTimeNormalBooking,
        maxTimeGeneralBooking,
      ].filter((m) => m !== null) as moment.Moment[]

      if (validTimes.length === 0) return null

      return moment.min(validTimes)
    }
    const minTimeA = getMinTime(maxTimeNormalBookingA, maxTimeGeneralBookingA)
    const minTimeB = getMinTime(maxTimeNormalBookingB, maxTimeGeneralBookingB)
    if (minTimeA?.isBefore(minTimeB)) return -1
    if (minTimeA?.isAfter(minTimeB)) return 1
    if (minTimeB?.isBefore(minTimeA)) return 1
    if (minTimeB?.isAfter(minTimeA)) return -1

    /**
     * 第3ソート：通常・総合の指示時刻など
     * -> 通常・総合指示時刻 ≥ 通常・総合予約時間 ≥ 通常総合受付時間
     */
    const INFINITY_TIME = moment("2999-12-31 23:59:59", DATE_TIME_FORMAT)

    // 第3-1ソート：指示時刻同士で比較
    const getMinInstructionTime = (
      booking: Booking
    ) => {
      const validTimes = [
        // 指示時刻
        moment(booking.entry_instruction_time, DATE_TIME_FORMAT),
        // 総合入場指示時刻
        moment(booking.general_entry_instruction_time, DATE_TIME_FORMAT),
        // 「日付ありと日付なし（空）の比較では日付ありがソート上位」としたいため、番兵として十分大きいダミー日付を配置しておく
        INFINITY_TIME,
      ].filter((m) => m.isValid())
      return moment.min(validTimes)
    }

    const minInstructionTimeA = getMinInstructionTime(a)
    const minInstructionTimeB = getMinInstructionTime(b)
    if (minInstructionTimeA.isBefore(minInstructionTimeB)) return -1
    if (minInstructionTimeA.isAfter(minInstructionTimeB)) return 1

    // 第3-2ソート：予約開始時間同士で比較
    const getMinStartTime = (
      booking: Booking
    ) => {
      const validTimes = [
        // 予約開始時間
        moment(`${booking.plan_start_date} ${booking.plan_start_time}`, DATE_TIME_FORMAT_API),
        // 総合予約開始時間
        moment(booking.general_booking_start_date, DATE_TIME_FORMAT),
        // 「日付ありと日付なし（空）の比較では日付ありがソート上位」としたいため、番兵として十分大きいダミー日付を配置しておく
        INFINITY_TIME,
      ].filter((m) => m.isValid())
      return moment.min(validTimes)
    }

    const minStartTimeA = getMinStartTime(a)
    const minStartTimeB = getMinStartTime(b)
    if (minStartTimeA.isBefore(minStartTimeB)) return -1
    if (minStartTimeA.isAfter(minStartTimeB)) return 1

    // 第3-3ソート：受付時間同士で比較
    const getMinCheckinTime = (
      booking: Booking
    ) => {
      const validTimes = [
        // 受付時間
        moment(booking.checkin_date, DATE_TIME_FORMAT),
        // 総合受付時間
        moment(booking.checkin_date_of_general_booking, DATE_TIME_FORMAT),
        // 「日付ありと日付なし（空）の比較では日付ありがソート上位」としたいため、番兵として十分大きいダミー日付を配置しておく
        INFINITY_TIME,
      ].filter((m) => m.isValid())
      return moment.min(validTimes)
    }

    const minCheckinTimeA = getMinCheckinTime(a)
    const minCheckinTimeB = getMinCheckinTime(b)
    if (minCheckinTimeA.isBefore(minCheckinTimeB)) return -1
    if (minCheckinTimeA.isAfter(minCheckinTimeB)) return 1

    return 0
  })

  return result
}
